import { AnimatePresence, motion } from "framer-motion"
import { X } from "lucide-react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useClickOutside } from "../../travel_book/avia_v3/hooks"
import { formatDateDivider } from "../../utils/date/format"
import ChatHeader from "./ChatHeader"
import ChatInfoSidebar from "./ChatInfoSidebar"
import ChatListSidebar from "./ChatListSidebar"
import ComposeArea from "./ComposeArea"
import AiChatComposer from "./AiChatComposer"
import {
  buildStandardThemes,
  CHAT_TABS,
  FOR_ADMIN_KEY,
  MOTION_VARIANTS,
  STANDARD_THEME_IDS,
  TAB_CAPABILITIES,
  TAB_FILTER_DEFAULTS,
  TO_CLIENT_KEY,
} from "./constants"
import DateDivider from "./DateDivider"
import EmptyState from "./EmptyState"
import MessageBubble from "./MessageBubble"

import {
  useCreateNotifyMutation,
  useGetChatMetaQuery,
  useGetChatNotifiesQuery,
  useGetListFlightChangesQuery,
  useLazyGetChatDetailQuery,
  useLazyGetChatNotifiesQuery,
  useLazyGetFlightChangesDetailQuery,
  useLazyGetListFlightChangesQuery,
  useToggleReadMessageMutation,
  useUpdateNotifyDepartmentMutation,
  useUpdateNotifyMessageMutation,
} from "../../../app/services/book/notifies"

import {
  useCreateLeadNotifyMutation,
  useGetLeadNotifiesQuery,
  useLazyGetLeadChatDetailQuery,
  useLazyGetLeadNotifiesQuery,
  useToggleLeadReadMessageMutation,
} from "../../../app/services/book/leadNotifies"

import {
  useDeleteAiChatBotConversationMutation,
  useRestoreAiChatBotConversationMutation,
  useStartNewAiChatBotConversationMutation,
  useGetAiChatBotConversationsQuery,
  useLazyGetAiChatBotConversationDetailQuery,
  useLazyGetAiChatBotConversationsQuery,
} from "../../../app/services/book/aiChatBot"

import {
  markChatAsRead,
  removeChat,
  resetNotifies,
  selectNotifiesChatMeta,
  selectNotifiesHasMore,
  selectNotifiesList,
  setNotifies,
} from "../../../app/reducers/book/notifies"

import { selectDefaultDepartament } from "../../../app/reducers/admin/departamentsSlice"
import { adjustUnreadCount, bumpChat } from "../../../app/reducers/book/notifies"
import { useEventBusModal } from "../../../shared/hooks"
import { READ_MESSAGE_STATUS } from "../../utils/variables/chat"
import { useChatMessages, useChatPanelResize, useChatSidebarResize } from "../hooks"
import useChatComposeResize from "../hooks/useChatComposeResize"
import { useChatNotificationsSocket } from "../hooks/useChatNotificationsSocket"
import { useChatRoomSocket } from "../hooks/useChatRoomSocket"
import { useDraftPersist } from "../hooks/useDraftPersist"
import { setRussianAllowed } from "./aiLanguages"

const POLL_INTERVAL = 5 * 60 * 1000

const LAST_FILTERS_KEY = (tab) => `notifies_last_filters_${tab}`

const loadLastFilters = (tab) => {
  try {
    const raw = localStorage.getItem(LAST_FILTERS_KEY(tab))
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

const saveLastFilters = (tab, filters) => {
  try {
    const { page, per_page, bundle_id, lead_id, archived, ...rest } = filters
    localStorage.setItem(LAST_FILTERS_KEY(tab), JSON.stringify(rest))
  } catch (_) {}
}

const clearLastFilters = (tab) => {
  try {
    localStorage.removeItem(LAST_FILTERS_KEY(tab))
  } catch (_) {}
}

const buildInitialFilters = (tab) => {
  const defaults = TAB_FILTER_DEFAULTS[tab] ?? TAB_FILTER_DEFAULTS[CHAT_TABS.ALL]
  const saved = loadLastFilters(tab)
  if (!saved) return { ...defaults }
  return { ...defaults, ...saved, page: 1, per_page: defaults.per_page, bundle_id: "", lead_id: "" }
}

const getFilterDepartmentIds = (filters) => {
  const ids =
    Array.isArray(filters?.departament_ids) && filters.departament_ids.length > 0
      ? filters.departament_ids
      : filters?.departament_id
        ? [filters.departament_id]
        : []
  return ids.map(String)
}

const chatId = (chat) => chat?.bundle_id ?? chat?.lead_id ?? null

const presenceKey = (chatType, id) => `${chatType}_${id}`

const MessageFilter = ({ departments, filterDepts, setFilterDepts, messages }) => {
  const { t } = useTranslation()
  const counts = useMemo(() => {
    const map = {}
    messages.forEach((msg) => {
      if (msg.department_id) map[msg.department_id] = (map[msg.department_id] || 0) + 1
    })
    return map
  }, [messages])
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => counts[d.id] > 0),
    [departments, counts]
  )
  if (activeDepts.length === 0) return null
  const toggle = (id) => {
    const sid = String(id)
    setFilterDepts((prev) => (prev.includes(sid) ? prev.filter((d) => d !== sid) : [...prev, sid]))
  }
  const isActive = (id) => filterDepts.includes(String(id))
  const hasFilter = filterDepts.length > 0
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-slate-100 overflow-x-auto scrollbar-none flex-shrink-0">
      <button
        onClick={() => setFilterDepts([])}
        className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
          !hasFilter
            ? "bg-slate-800 text-white shadow-sm"
            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
        }`}
      >
        {t("admin.all", "Все")}
        <span className={`ml-1.5 text-[10px] ${!hasFilter ? "text-white/70" : "text-slate-400"}`}>
          {messages.length}
        </span>
      </button>
      {activeDepts.map((dept) => (
        <button
          key={dept.id}
          onClick={() => toggle(dept.id)}
          className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
            isActive(dept.id)
              ? "bg-blue-500 text-white shadow-sm"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {dept.name}
          <span
            className={`ml-1.5 text-[10px] ${isActive(dept.id) ? "text-white/70" : "text-slate-400"}`}
          >
            {counts[dept.id]}
          </span>
        </button>
      ))}
      {hasFilter && (
        <button
          onClick={() => setFilterDepts([])}
          className="flex-shrink-0 p-1 hover:bg-red-50 rounded-full transition-colors ml-auto"
        >
          <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
        </button>
      )}
    </div>
  )
}

const useTabListQuery = (activeTab, filters, skip) => {
  const allQuery = useGetChatNotifiesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.ALL,
    refetchOnMountOrArgChange: true,
  })
  const flightQuery = useGetListFlightChangesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.FLIGHT_CHANGES,
    refetchOnMountOrArgChange: true,
  })
  const leadsQuery = useGetLeadNotifiesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.LEADS,
    refetchOnMountOrArgChange: true,
  })
  const aiBotQuery = useGetAiChatBotConversationsQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.AI_BOT,
    refetchOnMountOrArgChange: true,
  })

  switch (activeTab) {
    case CHAT_TABS.FLIGHT_CHANGES:
      return flightQuery
    case CHAT_TABS.LEADS:
      return leadsQuery
    case CHAT_TABS.AI_BOT:
      return aiBotQuery
    default:
      return allQuery
  }
}

const useTabDetailQuery = (activeTab) => {
  const [getChatDetail] = useLazyGetChatDetailQuery()
  const [getFlightChangesDetail] = useLazyGetFlightChangesDetailQuery()
  const [getLeadChatDetail] = useLazyGetLeadChatDetailQuery()
  const [getAiChatBotConversationDetail] = useLazyGetAiChatBotConversationDetailQuery()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.FLIGHT_CHANGES:
        return getFlightChangesDetail
      case CHAT_TABS.LEADS:
        return getLeadChatDetail
      case CHAT_TABS.AI_BOT:
        return getAiChatBotConversationDetail
      default:
        return getChatDetail
    }
  }, [activeTab])
}

const useTabLazyListQuery = (activeTab) => {
  const [getChatNotifiesLazy] = useLazyGetChatNotifiesQuery()
  const [getFlightChangesLazy] = useLazyGetListFlightChangesQuery()
  const [getLeadNotifiesLazy] = useLazyGetLeadNotifiesQuery()
  const [getAiChatBotConversationsLazy] = useLazyGetAiChatBotConversationsQuery()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.FLIGHT_CHANGES:
        return getFlightChangesLazy
      case CHAT_TABS.LEADS:
        return getLeadNotifiesLazy
      case CHAT_TABS.AI_BOT:
        return getAiChatBotConversationsLazy
      default:
        return getChatNotifiesLazy
    }
  }, [activeTab])
}

const useTabCreateNotify = (activeTab) => {
  const [createBundleNotify, bundleState] = useCreateNotifyMutation()
  const [createLeadNotify, leadState] = useCreateLeadNotifyMutation()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.LEADS:
        return [createLeadNotify, leadState]
      default:
        return [createBundleNotify, bundleState]
    }
  }, [activeTab])
}

const useTabToggleRead = (activeTab) => {
  const [toggleBundle] = useToggleReadMessageMutation()
  const [toggleLead] = useToggleLeadReadMessageMutation()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.LEADS:
        return toggleLead
      default:
        return toggleBundle
    }
  }, [activeTab])
}

const ChatBox = ({ modalId, isAdmin, currentCompanyId, currentUserId, themeName }) => {
  const IS_TRAVELON = themeName === "novatravel"
  const OPEN_EVENT = `modal:${modalId}:open`
  const CLOSE_EVENT = `modal:${modalId}:close`

  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const locale = i18n.locale

  const { isOpen, setIsOpen, payload, openTick } = useEventBusModal(OPEN_EVENT, CLOSE_EVENT, [
    modalId,
  ])

  const { bundleId, typeNotify, providerId, remoteOrder, leadId } = payload ?? {}

  const anchorLoadingRef = useRef(false)
  if (isOpen && (bundleId || leadId)) {
    anchorLoadingRef.current = true
  }

  const { list: notifiesList, meta: notifiesMeta } = useSelector(selectNotifiesList)
  const hasMore = useSelector(selectNotifiesHasMore)
  const {
    departments,
    messagethemes: messagethemesData,
    total_unread,
    leads_unread,
    ai_unread,
    settings,
  } = useSelector(selectNotifiesChatMeta)

  const defaultDepartment = useSelector(selectDefaultDepartament)

  const [selectedChat, setSelectedChat] = useState(null)
  const [deleteAiConversation] = useDeleteAiChatBotConversationMutation()
  const [restoreAiConversation] = useRestoreAiChatBotConversationMutation()

  const handleRestoreAiChat = async (chat) => {
    try {
      await restoreAiConversation(chat.id).unwrap()
      dispatch(removeChat(chat.id))
      if (selectedChat?.id === chat.id) setSelectedChat(null)
    } catch (_error) {
      return
    }
  }

  const handleDeleteAiChat = async (chat) => {
    try {
      await deleteAiConversation(chat.id).unwrap()
      dispatch(removeChat(chat.id))
      if (selectedChat?.id === chat.id) setSelectedChat(null)
    } catch (_error) {
      return
    }
  }
  const [isMobileView, setIsMobileView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [message, setMessage] = useState("")
  const [department, setDepartment] = useState(null)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [messageTheme, setMessageTheme] = useState(null)
  const [isAnchorLoading, setIsAnchorLoading] = useState(false)
  const [filterDepts, setFilterDepts] = useState([])
  const [sendMode, setSendMode] = useState("all")
  const [replyTo, setReplyTo] = useState(null)
  const [presenceMap, setPresenceMap] = useState({})

  const [activeTab, setActiveTab] = useState(CHAT_TABS.ALL)
  const [filters, setFilters] = useState(() => buildInitialFilters(CHAT_TABS.ALL))

  // Модалку открыли с явной вкладкой (payload.tab). Пока вкладка не применена
  // для ТЕКУЩЕГО открытия (appliedTabTick !== openTick), запрос списка пропускаем:
  // иначе запрос ALL-вкладки вернётся позже и перезапишет список в redux чужими чатами.
  // После применения флаг снимается, и ручное переключение вкладок работает как обычно.
  const [appliedTabTick, setAppliedTabTick] = useState(null)
  const requestedTab =
    payload?.tab &&
    !payload?.bundleId &&
    !payload?.leadId &&
    Object.values(CHAT_TABS).includes(payload.tab)
      ? payload.tab
      : null
  const tabPending = Boolean(isOpen && requestedTab && appliedTabTick !== openTick)

  const tabCapabilities = TAB_CAPABILITIES[activeTab] ?? TAB_CAPABILITIES[CHAT_TABS.ALL]
  const activeChatType = activeTab === CHAT_TABS.LEADS ? "lead" : "bundle"

  const filterDepartmentIds = useMemo(
    () => (tabCapabilities.canFilterByDepartment ? getFilterDepartmentIds(filters) : []),
    [filters, tabCapabilities.canFilterByDepartment]
  )

  const chatRef = useRef(null)
  const sidebarRef = useRef(null)
  const messagesEndRef = useRef(null)
  const firstUnreadRef = useRef(null)
  const prevMsgCountRef = useRef(0)
  const sentinelRef = useRef(null)
  const scrolledForBundleRef = useRef(null)
  const scrollTimerRef = useRef(null)

  const setAnchorLoading = useCallback((val) => {
    anchorLoadingRef.current = val
    setIsAnchorLoading(val)
  }, [])

  const { isFetching, refetch: refetchList } = useTabListQuery(
    activeTab,
    filters,
    !isOpen || anchorLoadingRef.current || tabPending
  )

  const getChatDetail = useTabDetailQuery(activeTab)
  const getListLazy = useTabLazyListQuery(activeTab)

  const [getLeadNotifiesLazy] = useLazyGetLeadNotifiesQuery()
  const [getLeadChatDetailDirect] = useLazyGetLeadChatDetailQuery()

  const [createNotify, { isFetching: createIsFetching }] = useTabCreateNotify(activeTab)
  const toggleReadMsg = useTabToggleRead(activeTab)

  useGetChatMetaQuery(
    { departament_ids: filterDepartmentIds },
    { skip: !isOpen, refetchOnMountOrArgChange: true }
  )

  const [updateDepartment] = useUpdateNotifyDepartmentMutation()
  const [updateNotifyMessage] = useUpdateNotifyMessageMutation()

  const { sidebarWidth, setSidebarWidth, isResizing, startResize, collapse, expand } =
    useChatSidebarResize(15)
  const { chatWidth, isResizingChat, startResizeChat } = useChatPanelResize(80)

  const { composeHeight, isResizingCompose, startResizeCompose } = useChatComposeResize(200)

  const { saveDraftDebounced, loadDraft, clearDraft } = useDraftPersist()

  useEffect(() => {
    if (bundleId || leadId) setSidebarWidth(0)
    else setSidebarWidth((prev) => (prev === 0 ? 15 : prev))
  }, [bundleId, leadId])

  const {
    getMessages,
    getChatMeta,
    fetchMessages,
    isLoading,
    sendMessage,
    toggleEdit,
    saveEdit,
    cancelEdit,
    changeDept,
    toggleReadStatus,
    markAsRead,
    receiveMessage,
    applyReadStatus,
    applyDepartmentChange,
  } = useChatMessages({
    departments,
    getChatDetail,
    createNotify,
    locale,
    currentCompanyId,
    currentUserId,
    sortByLastMessage: settings?.sort_by_last_message,
  })

  const selectedChatId = chatId(selectedChat)
  // Флаг берём из chat_detail (актуальнее: окно ответа Messenger могло закрыться), а до его загрузки —
  // из списка чатов; чат, пришедший через ActionCable, попадает в список без client_linked.
  const selectedChatMeta = getChatMeta(selectedChatId)
  const canSendToClient = Boolean(
    tabCapabilities.isLeads &&
    (selectedChatMeta ? selectedChatMeta.client_linked : selectedChat?.client_linked)
  )
  const clientChannel =
    selectedChatMeta?.client_channel ?? selectedChat?.client_channel ?? "telegram"
  const clientReplyWindow = tabCapabilities.isLeads
    ? (selectedChatMeta?.client_reply_window ?? null)
    : null

  // Для лидов из бота по умолчанию отвечаем клиенту; ручной выбор режима живёт до смены чата.
  // В чатах без привязки к боту режим «клиенту» недоступен — возвращаем «всем».
  const sendModeDefaultedForRef = useRef(null)
  useEffect(() => {
    if (!canSendToClient) {
      sendModeDefaultedForRef.current = null
      if (sendMode === TO_CLIENT_KEY) setSendMode("all")
      return
    }
    if (sendModeDefaultedForRef.current !== selectedChatId) {
      sendModeDefaultedForRef.current = selectedChatId
      setSendMode(TO_CLIENT_KEY)
    }
  }, [sendMode, canSendToClient, selectedChatId])
  const allMessages = selectedChat ? getMessages(selectedChatId) : []
  const currentMessages =
    filterDepts.length > 0
      ? allMessages.filter((msg) => filterDepts.includes(String(msg.department_id)))
      : allMessages
  const currentMessagesLength = currentMessages.length

  const firstUnreadIndex = useMemo(
    () => currentMessages.findIndex((msg) => msg.status !== READ_MESSAGE_STATUS && !msg.isOwn),
    [currentMessages]
  )

  setRussianAllowed(settings?.ai_russian_allowed)
  const useDistribution = settings?.distribution_of_notifications_by_departments
  const hideStandard = settings?.hide_standard_notification_themes

  const isCurrentChatLoading = isLoading(selectedChatId)

  const filteredMessagethemes = useMemo(() => {
    if (tabCapabilities.isLeads) return []

    const standard = hideStandard ? [] : buildStandardThemes(t, isAdmin)
    const custom = (messagethemesData || []).filter((theme) => {
      if (IS_TRAVELON && theme.only_admin && !isAdmin) return false
      if (useDistribution) {
        if (!department) return false
        return Number(theme.departament_id) === Number(department)
      }
      return true
    })
    return [...standard, ...custom]
  }, [
    messagethemesData,
    department,
    isAdmin,
    useDistribution,
    hideStandard,
    t,
    tabCapabilities.isLeads,
  ])

  useChatRoomSocket({
    chatType: activeChatType,
    chatId: selectedChatId,
    onMessage: useCallback(
      (data) => {
        if (!selectedChatId) return
        receiveMessage(selectedChatId, data.notify)

        const isOwn = data.notify?.user_id === currentUserId
        if (settings?.sort_by_last_message) {
          dispatch(
            bumpChat({
              bundle_id: activeChatType === "lead" ? undefined : selectedChatId,
              lead_id: activeChatType === "lead" ? selectedChatId : undefined,
              message: data.notify?.message ?? "",
              created_at: data.notify?.created_at ?? new Date().toISOString(),
            })
          )
        }
        if (!isOwn) {
          dispatch(
            adjustUnreadCount({
              bundle_id: activeChatType === "lead" ? undefined : selectedChatId,
              lead_id: activeChatType === "lead" ? selectedChatId : undefined,
              delta: 1,
            })
          )
        }
      },
      [
        selectedChatId,
        activeChatType,
        currentUserId,
        settings?.sort_by_last_message,
        dispatch,
        receiveMessage,
      ]
    ),
    onReadStatus: useCallback(
      (data) => {
        if (!selectedChatId) return

        const msg = getMessages(selectedChatId).find((m) => m.id === data.notify_id)
        if (msg && !msg.isOwn) {
          const wasRead = msg.status === READ_MESSAGE_STATUS
          const inScope =
            filterDepartmentIds.length === 0 ||
            filterDepartmentIds.includes(String(msg.department_id))

          if (inScope && wasRead !== data.is_read) {
            dispatch(
              adjustUnreadCount({
                [activeChatType === "lead" ? "lead_id" : "bundle_id"]: selectedChatId,
                delta: data.is_read ? -1 : 1,
              })
            )
          }
        }

        applyReadStatus(selectedChatId, data.notify_id, data.is_read, data.read_log?.history)
      },
      [selectedChatId, activeChatType, filterDepartmentIds, getMessages, applyReadStatus, dispatch]
    ),
    // приход события 'department_change' (см. Chat::ChatBroadcaster.broadcast_department_change)
    onDepartmentChange: useCallback(
      (data) => {
        if (!selectedChatId) return

        const msg = getMessages(selectedChatId).find((m) => m.id === data.notify_id)
        if (
          msg &&
          msg.status !== READ_MESSAGE_STATUS &&
          !msg.isOwn &&
          filterDepartmentIds.length > 0
        ) {
          const wasIn = filterDepartmentIds.includes(String(msg.department_id))
          const isIn = filterDepartmentIds.includes(String(data.departament_id))
          if (wasIn !== isIn) {
            dispatch(
              adjustUnreadCount({
                [activeChatType === "lead" ? "lead_id" : "bundle_id"]: selectedChatId,
                delta: isIn ? 1 : -1,
              })
            )
          }
        }

        applyDepartmentChange(selectedChatId, data.notify_id, data.departament_id, data.department)
      },
      [
        selectedChatId,
        activeChatType,
        filterDepartmentIds,
        getMessages,
        applyDepartmentChange,
        dispatch,
      ]
    ),
    onPresence: useCallback(
      (data) => {
        setPresenceMap((prev) => ({
          ...prev,
          [presenceKey(data.chat_type, data.chat_id)]: (data.users || []).filter(
            (u) => u.id !== currentUserId
          ),
        }))
      },
      [currentUserId]
    ),
  })

  useChatNotificationsSocket({
    enabled: isOpen,
    onNewMessage: useCallback(
      (data) => {
        if (data.chat_id === selectedChatId && data.chat_type === activeChatType) return

        if (activeTab === CHAT_TABS.AI_BOT || data.chat_type !== activeChatType) return

        if (
          tabCapabilities.canFilterByDepartment &&
          Array.isArray(filters.departament_ids) &&
          filters.departament_ids.length > 0
        ) {
          const departmentMatches = filters.departament_ids
            .map(String)
            .includes(String(data.notify?.departament_id))
          if (!departmentMatches) return
        }

        if (filters.status === "unread" && data.notify?.is_read === true) return
        if (filters.status === "read" && data.notify?.is_read === false) return

        const isOwn = data.notify?.user_id === currentUserId
        dispatch(
          bumpChat({
            bundle_id: data.chat_type === "lead" ? undefined : data.chat_id,
            lead_id: data.chat_type === "lead" ? data.chat_id : undefined,
            message: data.notify?.message ?? "",
            created_at: data.notify?.created_at ?? new Date().toISOString(),
            user_name: data.notify?.name ?? data.notify?.user_name ?? null,
            departament_id: data.notify?.departament_id ?? null,
            state_name: data.notify?.state_name ?? null,
            days_to_flight: data.notify?.days_to_flight ?? null,
            unread_count: isOwn ? 0 : 1,
            extra:
              data.chat_type === "lead"
                ? {
                    client_linked: data.notify?.client_linked ?? false,
                    client_channel: data.notify?.client_channel ?? null,
                  }
                : undefined,
          })
        )
        if (!isOwn) {
          dispatch(
            adjustUnreadCount({
              bundle_id: data.chat_type === "lead" ? undefined : data.chat_id,
              lead_id: data.chat_type === "lead" ? data.chat_id : undefined,
              delta: 1,
            })
          )
        }
      },
      [
        selectedChatId,
        activeChatType,
        activeTab,
        currentUserId,
        dispatch,
        tabCapabilities.canFilterByDepartment,
        filters.departament_ids,
        filters.status,
      ]
    ),
    onPresence: useCallback(
      (data) => {
        setPresenceMap((prev) => ({
          ...prev,
          [presenceKey(data.chat_type, data.chat_id)]: (data.users || []).filter(
            (u) => u.id !== currentUserId
          ),
        }))
      },
      [currentUserId]
    ),
  })

  useEffect(() => {
    if (!notifiesList?.length) return
    setPresenceMap((prev) => {
      const next = { ...prev }
      notifiesList.forEach((chat) => {
        const id = chatId(chat)
        if (id == null || !Array.isArray(chat.presence)) return
        const key = presenceKey(activeChatType, id)
        if (!next[key] || next[key].length <= chat.presence.length) {
          next[key] = chat.presence.filter((u) => u.id !== currentUserId)
        }
      })
      return next
    })
  }, [notifiesList, activeChatType, currentUserId])

  useEffect(() => {
    if (isAdmin) return
    if (!selectedChat || isCurrentChatLoading) return

    const timer = setTimeout(() => {
      markAsRead(selectedChatId, { toggleReadMsg, departmentIds: filterDepartmentIds })
    }, 0)

    return () => clearTimeout(timer)
  }, [selectedChatId, isCurrentChatLoading])

  useEffect(() => {
    if (!selectedChat) return
    scrolledForBundleRef.current = null
    prevMsgCountRef.current = 0
    firstUnreadRef.current = null
    setMessage("")
    setAttachedFiles([])
    if (
      typeNotify &&
      [STANDARD_THEME_IDS.CANCELED, STANDARD_THEME_IDS.CORRECTION].includes(typeNotify)
    ) {
      setMessageTheme(typeNotify)
    } else {
      setMessageTheme(null)
    }
    setDepartment(null)
    setShowSidebar(false)
    setReplyTo(null)
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current)
      scrollTimerRef.current = null
    }
  }, [selectedChatId, typeNotify])

  useEffect(() => {
    if (!selectedChat) return
    setFilterDepts(filterDepartmentIds)
  }, [selectedChatId, filterDepartmentIds])

  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === activeTab) return
      setSelectedChat(null)
      setIsMobileView(false)
      dispatch(resetNotifies())
      setActiveTab(newTab)
      setFilters(buildInitialFilters(newTab))
    },
    [activeTab, dispatch]
  )

  // Открытие модалки с явной вкладкой (например, из кнопки AI-чата при включённом ai_search):
  // window.EventBus.emit("modal:chatbox:open", { tab: "ai_bot" })
  useEffect(() => {
    if (!isOpen || !requestedTab) return
    handleTabChange(requestedTab)
    setAppliedTabTick(openTick)
  }, [openTick])

  const handleSelectChat = useCallback(
    async (chat) => {
      setSelectedChat(chat)
      setReplyTo(null)
      if (isMobile) setIsMobileView(true)
      await fetchMessages(chatId(chat))
      if (chat.ai) dispatch(markChatAsRead({ bundle_id: chat.bundle_id }))

      const draft = loadDraft(chatId(chat))
      if (draft) {
        setMessage(draft.message || "")
        setDepartment(draft.department ?? null)
        setMessageTheme(draft.messageTheme ?? null)
      } else {
        setMessage("")
        setDepartment(null)
        setMessageTheme(null)
      }
    },
    [isMobile, fetchMessages, loadDraft]
  )

  const [startNewAiConversation] = useStartNewAiChatBotConversationMutation()

  const handleNewAiChat = async () => {
    try {
      const result = await startNewAiConversation().unwrap()
      if (filters.page === 1 && !filters.archived) refetchList()
      else setFilters((prev) => ({ ...prev, page: 1, archived: "" }))
      handleSelectChat({
        id: result.conversation_id,
        bundle_id: result.conversation_id,
        can_write: true,
      })
    } catch (_error) {
      return
    }
  }

  useEffect(() => {
    if (!isOpen || !payload?.bundleId) return
    const openByAnchor = async () => {
      setAnchorLoading(true)
      dispatch(resetNotifies())
      const defaults = TAB_FILTER_DEFAULTS[activeTab] ?? TAB_FILTER_DEFAULTS[CHAT_TABS.ALL]
      try {
        const result = await getListLazy({
          ...defaults,
          anchor_bundle_id: payload.bundleId,
        }).unwrap()
        if (result.chat) {
          dispatch(setNotifies({ items: result.data, meta: result.meta }))
          await handleSelectChat(result.chat)
          setFilters((prev) => ({ ...prev, page: result.meta.current_page + 1 }))
        } else {
          const emptyChat = {
            bundle_id: payload.bundleId,
            message: "",
            company_name: "",
            unread_count: 0,
            created_at: null,
            updated_at: null,
          }
          await handleSelectChat(emptyChat)
        }
      } catch (err) {
        console.error("openByAnchor error", err)
        setFilters({ ...defaults })
      } finally {
        setAnchorLoading(false)
      }
    }
    openByAnchor()
  }, [openTick])

  useEffect(() => {
    if (!isOpen || !payload?.leadId) return

    const openByLeadAnchor = async () => {
      setAnchorLoading(true)
      dispatch(resetNotifies())
      setActiveTab(CHAT_TABS.LEADS)

      const defaults = TAB_FILTER_DEFAULTS[CHAT_TABS.LEADS]
      setFilters({ ...defaults, lead_id: String(payload.leadId) })

      try {
        const result = await getLeadNotifiesLazy({
          ...defaults,
          lead_id: String(payload.leadId),
        }).unwrap()

        const chat = result.chat ??
          (result.data?.length > 0 ? result.data[0] : null) ?? {
            bundle_id: payload.leadId,
            message: "",
            lead_name: "",
            unread_count: 0,
            created_at: null,
            updated_at: null,
          }

        setSelectedChat(chat)
        if (isMobile) setIsMobileView(true)

        await fetchMessages(chatId(chat), false, getLeadChatDetailDirect)

        if (result.data && result.meta) {
          dispatch(setNotifies({ items: result.data, meta: result.meta }))
          setFilters({
            ...defaults,
            lead_id: String(payload.leadId),
            page: result.meta.current_page + 1,
          })
        }
      } catch (err) {
        console.error("openByLeadAnchor error", err)
        setFilters({ ...TAB_FILTER_DEFAULTS[CHAT_TABS.LEADS] })
      } finally {
        setAnchorLoading(false)
      }
    }

    openByLeadAnchor()
  }, [openTick])

  useEffect(() => {
    if (!isOpen) {
      anchorLoadingRef.current = false
      setIsAnchorLoading(false)
      setSelectedChat(null)
      setIsMobileView(false)
      dispatch(resetNotifies())
      // Каждое открытие стартует с вкладки ALL (или с вкладки из payload.tab)
      setActiveTab(CHAT_TABS.ALL)
      setFilters(buildInitialFilters(CHAT_TABS.ALL))
      setAppliedTabTick(null)
      setAttachedFiles([])
      setReplyTo(null)
      setPresenceMap({})
    }
  }, [isOpen])

  useEffect(() => {
    if (!selectedChat) return
    const bId = selectedChatId
    if (scrolledForBundleRef.current !== bId) return
    if (currentMessagesLength > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
    prevMsgCountRef.current = currentMessagesLength
  }, [currentMessagesLength, selectedChatId])

  useEffect(() => {
    if (
      !selectedChat ||
      scrolledForBundleRef.current === selectedChatId ||
      currentMessagesLength === 0
    )
      return
    const bId = selectedChatId
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    scrollTimerRef.current = setTimeout(() => {
      if (scrolledForBundleRef.current === bId) return
      if (firstUnreadIndex !== -1 && firstUnreadRef.current) {
        firstUnreadRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }
      scrolledForBundleRef.current = bId
      prevMsgCountRef.current = currentMessagesLength
      scrollTimerRef.current = null
    }, 200)
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current)
        scrollTimerRef.current = null
      }
    }
  }, [currentMessagesLength, firstUnreadIndex, selectedChatId])

  const handleDepartmentChange = useCallback((newDept) => {
    setDepartment(newDept)
    setMessageTheme(null)
  }, [])

  const handleFilterChange = useCallback((key, value) => {
    dispatch(resetNotifies())
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }, [])

  const handleFilterSearch = useCallback(() => {
    setFilters((prev) => {
      saveLastFilters(activeTab, prev)
      return prev
    })
  }, [activeTab])

  const handleFilterReset = useCallback(() => {
    clearLastFilters(activeTab)
    setFilters({ ...TAB_FILTER_DEFAULTS[activeTab] })
  }, [activeTab])

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
  }, [isFetching, hasMore])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore()
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  useClickOutside(chatRef, () => window.EventBus?.emit(CLOSE_EVENT))

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setIsMobileView(false)
    }
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  const handleBack = () => {
    setIsMobileView(false)
    setSelectedChat(null)
  }

  const handleReply = useCallback(
    (msg) => {
      setReplyTo(msg)
      if (msg?.department_id) setDepartment(msg.department_id)
      if (!IS_TRAVELON && msg?.message_theme) setMessageTheme(msg.message_theme)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    },
    [IS_TRAVELON]
  )

  const handleCancelReply = useCallback(() => setReplyTo(null), [])

  const handleSend = () => {
    if (!message.trim() || !selectedChat) return
    sendMessage(selectedChatId, {
      text: message,
      department,
      attachedFiles,
      theme: messageTheme,
      for_admin: sendMode === FOR_ADMIN_KEY,
      to_client: canSendToClient && sendMode === TO_CLIENT_KEY,
      chat_type: activeChatType,
      reply_to_id: replyTo?.id ?? null,
      provider_id: providerId ?? 0,
      remote_order: remoteOrder ?? 0,
    }).then((result) => {
      if (result?.clientDeliveryFailed) {
        window.alert(
          t("admin.client_delivery_failed", "Сообщение сохранено в чате, но не доставлено клиенту")
        )
      }
    })
    clearDraft(selectedChatId)
    setMessage("")
    setAttachedFiles([])
    setDepartment(null)
    setMessageTheme(null)
    setReplyTo(null)
  }

  const handleUpdateDepartment = async (notifyId, newDeptId) => {
    try {
      await updateDepartment({ id: notifyId, department_id: newDeptId }).unwrap()
      const foundDept = departments?.find((d) => d.id === newDeptId)
      changeDept(selectedChatId, notifyId, newDeptId, foundDept?.name ?? "")
    } catch (error) {
      console.error("Error updating department:", error)
    }
  }

  return (
    <div className="fixed top-0 left-0 z-30">
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              data-chat-panel
              key="backdrop"
              className="fixed inset-0 bg-black/20 z-40"
              variants={MOTION_VARIANTS.backdrop}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              key="panel"
              ref={chatRef}
              className={`fixed top-0 right-0 h-full bg-white shadow-2xl flex z-50 ${
                isResizing || isResizingChat || isResizingCompose ? "select-none" : ""
              }`}
              style={{
                width: isMobile ? "100%" : `${chatWidth}%`,
                minWidth: isMobile ? "100%" : "600px",
                maxWidth: isMobile ? "100%" : "95%",
              }}
              variants={MOTION_VARIANTS.panel}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {!isMobile && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize group z-10"
                  onMouseDown={startResizeChat}
                >
                  <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-16 bg-slate-300 group-hover:bg-blue-500 rounded-r transition-colors" />
                </div>
              )}

              <div className={`flex w-full h-full ${isMobile ? "" : "pl-1"}`}>
                <ChatListSidebar
                  sidebarRef={sidebarRef}
                  isMobileView={isMobileView}
                  selectedChat={selectedChat}
                  isMobile={isMobile}
                  sidebarWidth={sidebarWidth}
                  filteredChats={notifiesList}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onFilterReset={handleFilterReset}
                  onFilterSearch={handleFilterSearch}
                  activeTab={activeTab}
                  setActiveTab={handleTabChange}
                  onSelectChat={handleSelectChat}
                  onClose={() => setIsOpen(false)}
                  onStartResize={startResize}
                  isAdmin={isAdmin}
                  departments={departments}
                  sentinelRef={sentinelRef}
                  isLoadingMore={isFetching || isAnchorLoading}
                  hasMore={hasMore}
                  aiSearchEnabled={settings?.ai_search_enabled}
                  onCollapse={collapse}
                  onExpand={expand}
                  totalUnread={total_unread}
                  leadsUnread={leads_unread}
                  aiUnread={ai_unread}
                  messagethemes={messagethemesData}
                  presenceMap={presenceMap}
                  currentUserId={currentUserId}
                  onDeleteChat={activeTab === CHAT_TABS.AI_BOT ? handleDeleteAiChat : undefined}
                  onRestoreChat={activeTab === CHAT_TABS.AI_BOT ? handleRestoreAiChat : undefined}
                  onNewChat={activeTab === CHAT_TABS.AI_BOT ? handleNewAiChat : undefined}
                  onToggleArchive={
                    activeTab === CHAT_TABS.AI_BOT
                      ? () => {
                          setSelectedChat(null)
                          handleFilterChange("archived", filters.archived ? "" : "1")
                        }
                      : undefined
                  }
                />

                <motion.div
                  className={`${isMobileView && selectedChat ? "flex" : "hidden md:flex"} flex-1 flex-col bg-white min-w-0`}
                  variants={MOTION_VARIANTS.content}
                  initial="hidden"
                  animate="visible"
                >
                  {selectedChat ? (
                    <>
                      <ChatHeader
                        chat={selectedChat}
                        isMobile={isMobile}
                        onBack={handleBack}
                        onToggleSidebar={() => setShowSidebar((p) => !p)}
                      />

                      {tabCapabilities.showMessageFilter && (
                        <MessageFilter
                          departments={departments}
                          filterDepts={filterDepts}
                          setFilterDepts={setFilterDepts}
                          messages={allMessages}
                        />
                      )}

                      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-100">
                        {isLoading(selectedChatId) ? (
                          <div>{t("admin.loading_dots")}</div>
                        ) : (
                          currentMessages.map((msg, index) => {
                            const msgDate = msg.time ? msg.time.split(" |")[0] : null
                            const prevDate =
                              index > 0 ? currentMessages[index - 1].time?.split(" |")[0] : null
                            const showDivider = msgDate && msgDate !== prevDate
                            const isFirstUnread = index === firstUnreadIndex
                            return (
                              <React.Fragment key={msg.id}>
                                {showDivider && (
                                  <DateDivider
                                    label={formatDateDivider(
                                      msgDate.split(".").reverse().join("-"),
                                      locale
                                    )}
                                  />
                                )}
                                {isFirstUnread && (
                                  <div className="flex items-center gap-3 my-2 px-1">
                                    <div className="flex-1 h-px bg-blue-300" />
                                    <span className="text-xs text-blue-500 font-medium whitespace-nowrap select-none">
                                      {t("admin.unread_messages", "Непрочитанные сообщения")}
                                    </span>
                                    <div className="flex-1 h-px bg-blue-300" />
                                  </div>
                                )}
                                <div ref={isFirstUnread ? firstUnreadRef : null}>
                                  <MessageBubble
                                    msg={msg}
                                    clientChannel={clientChannel}
                                    isFirstUnread={isFirstUnread}
                                    onToggleEdit={(id) => toggleEdit(selectedChatId, id)}
                                    onSaveEdit={(id, text) =>
                                      saveEdit(selectedChatId, id, text, updateNotifyMessage)
                                    }
                                    onCancelEdit={(id) => cancelEdit(selectedChatId, id)}
                                    onChangeDept={
                                      tabCapabilities.isLeads
                                        ? null
                                        : (id, dept) => handleUpdateDepartment(id, dept)
                                    }
                                    toggleReadStatus={() =>
                                      toggleReadStatus(selectedChatId, msg.id, {
                                        toggleReadMsg,
                                        departmentIds: filterDepartmentIds,
                                      })
                                    }
                                    departments={tabCapabilities.isLeads ? [] : departments}
                                    isAdmin={isAdmin}
                                    currentCompanyId={currentCompanyId}
                                    onReply={tabCapabilities.canSendMessage ? handleReply : null}
                                    allMessages={allMessages}
                                  />
                                </div>
                              </React.Fragment>
                            )
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {activeTab === CHAT_TABS.AI_BOT && selectedChat?.can_write && (
                        <AiChatComposer
                          key={selectedChatId}
                          conversationId={selectedChatId}
                          onSent={() => fetchMessages(selectedChatId, true)}
                        />
                      )}

                      {tabCapabilities.canSendMessage && (
                        <ComposeArea
                          message={message}
                          setMessage={setMessage}
                          setDepartment={
                            tabCapabilities.isLeads ? undefined : handleDepartmentChange
                          }
                          attachedFiles={attachedFiles}
                          setAttachedFiles={setAttachedFiles}
                          onSend={handleSend}
                          departments={tabCapabilities.isLeads ? [] : departments}
                          department={tabCapabilities.isLeads ? null : department}
                          filteredMessagethemes={filteredMessagethemes}
                          setMessageTheme={tabCapabilities.isLeads ? undefined : setMessageTheme}
                          messageTheme={tabCapabilities.isLeads ? null : messageTheme}
                          setSendMode={setSendMode}
                          activeSendMode={sendMode}
                          isAdmin={isAdmin}
                          canSendToClient={canSendToClient}
                          clientChannel={clientChannel}
                          clientReplyWindow={clientReplyWindow}
                          replyTo={replyTo}
                          onCancelReply={handleCancelReply}
                          useDistribution={tabCapabilities.isLeads ? false : useDistribution}
                          messageSubjectIsRequired={
                            tabCapabilities.isLeads ? false : settings?.message_subject_is_required
                          }
                          composeHeight={composeHeight}
                          onStartResizeCompose={startResizeCompose}
                          onDraftChange={(data) => saveDraftDebounced(selectedChatId, data)}
                          bundleId={tabCapabilities.isLeads ? undefined : selectedChat?.bundle_id}
                          leadId={tabCapabilities.isLeads ? chatId(selectedChat) : undefined}
                          autofillStandardThemeIds={
                            settings?.autofill_description_for_standard_themes
                          }
                          autofillStandardThemesScope={
                            settings?.autofill_description_for_standard_themes_scope
                          }
                        />
                      )}
                    </>
                  ) : (
                    <EmptyState />
                  )}
                </motion.div>

                <AnimatePresence>
                  {showSidebar && selectedChat && !tabCapabilities.isLeads && (
                    <ChatInfoSidebar
                      key="info"
                      chat={selectedChat}
                      onClose={() => setShowSidebar(false)}
                    />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ChatBox
